import { useMemo, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import PieChart from "components/core/blocks/PieChart";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { formatDatamartFilters } from "utils/formatV2DatamartFilters";
import useHaloRank from "hooks/useHaloRank";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { ProductCell } from "views/merchandise/cells/ProductCell";
import { formatCurrency, formatNumber } from "utils/formatNumber";
import MultiMetricChart from "./MultiMetricChart";
import { buildMetric } from "views/merchandise/metrics";
import { useFetch } from "hooks/api";
import { useDates } from "dates/DatesContext";
import useSelectedMetrics from "./hooks/useSelectedMetrics";
import moment from "moment";
import Tabs from "components/core/basic/Tabs";
import { AdvertisingSection } from "views/merchandise/Product";
import ProgressBar from "components/core/basic/ProgressBar";
import { useModuleEnabled } from "redux/selectors/modules";
import { Button, buttonRecipe } from "components/core/basic/Button";
import CenteredModal from "components/core/basic/CenteredModal";
import AudienceForm from "views/amc/audience/AudienceForm";
import AdPlanModal from "components/core/blocks/AdPlan/AdPlanModal";
import { useCategoriesQuery } from "hooks/useCategories";
import { useAppSelector } from "redux/hooks";

const TABS = [
  { key: "details", label: "Details" },
  { key: "insights", label: "Insights" },
  { key: "advertising", label: "Advertising" },
];

const DEFAULT_TAB = TABS[0].key;

const Details = ({
  marketPlace,
  productChartData,
  productTotals,
  metrics,
  group,
  productId,
  metricsPosition = "top",
}) => {
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["orders_repeat", "orders_ntb", "sales_repeat", "sales_ntb"],
    "shopperinsightsProduct",
  );
  const { ranks: productRanks } = useHaloRank(marketPlace, {});

  const { customerValue, purchaseFreq, salesRepeat, salesNtb, brandCustomerValue } = useMemo(() => {
    if (productTotals?.length > 0) {
      const totals = productTotals?.[0];
      return {
        customerValue: totals?.customer_value,
        purchaseFreq: totals?.purchase_freq,
        salesRepeat: totals?.sales_repeat,
        salesNtb: totals?.sales_ntb,
        brandCustomerValue: totals?.brand_customer_value,
      };
    }
    return {};
  }, [productTotals]);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const Metrics = () => (
    <Col xs={12} className="d-flex justify-content-around mb-3 mt-5">
      <DetailedMetric
        center
        size="small"
        id={"cv"}
        name={"Sales New to Brand"}
        value={salesNtb}
        formatter={(v) => formatCurrency(v, marketPlace.marketPlace)}
      />
      <DetailedMetric
        center
        size="small"
        id={"cv"}
        name={"Sales Repeat"}
        value={salesRepeat}
        formatter={(v) => formatCurrency(v, marketPlace.marketPlace)}
      />
      <DetailedMetric
        center
        size="small"
        id={"cv"}
        name={"LTV"}
        value={customerValue}
        formatter={(v) => formatCurrency(v, marketPlace.marketPlace)}
      />
      <DetailedMetric
        center
        size="small"
        id={"brand_cv"}
        name={"Brand LTV"}
        value={brandCustomerValue}
        formatter={(v) => formatCurrency(v, marketPlace.marketPlace)}
      />
      <DetailedMetric
        center
        size="small"
        id={"purchase_frequency"}
        name={"Purchase Frequency"}
        value={purchaseFreq}
        formatter={(v) => (v ? v.toFixed(0) + " days" : "-")}
      />
      <DetailedMetric
        center
        size="small"
        id={"halo_rank"}
        name={"Halo Rank"}
        value={productRanks?.[productId]}
        formatter={(v) => (v ? v.toFixed(0) : "-")}
      />
    </Col>
  );

  return (
    <>
      {metricsPosition === "top" && <Metrics />}
      <Col xs={12} className="mb-5">
        {productChartData?.length > 0 && (
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={productChartData}
            group={group}
            handleLegendChange={handleLegendChange}
          />
        )}
      </Col>
      {metricsPosition === "bottom" && <Metrics />}
    </>
  );
};

const Insights = ({ marketPlace, productTotals, metrics }) => {
  const tableRef = useRef();

  const relatedProducts = useMemo(() => {
    if (productTotals?.length > 0) {
      const lastRow = productTotals[0];
      return (
        lastRow?.also_bought?.reduce((agg, p) => {
          // merge all the also_bought products, sum product totals
          for (const [k, v] of Object.entries(p)) {
            agg[k] = (agg[k] ?? 0) + v;
          }
          return agg;
        }, {}) ?? {}
      );
    }
    return {};
  }, [productTotals]);

  const { salesRepeat, salesNtb } = useMemo(() => {
    if (productTotals?.length > 0) {
      const totals = productTotals?.[0];
      return {
        salesRepeat: totals?.sales_repeat,
        salesNtb: totals?.sales_ntb,
      };
    }
    return {};
  }, [productTotals]);

  const dt = moment().add(50, "days").format("YYYY-MM-DD");

  const relatedProductIds = Object.keys(relatedProducts);

  const { data: relatedProductsData } = useFetch(
    ["products", marketPlace.marketPlace, dt, relatedProducts],
    "/api/data_report/product_report",
    {
      mp: marketPlace.marketPlace,
      filters: "product_ids:" + relatedProductIds.join(","),
      start_date: dt,
      end_date: dt,
    },
    {
      select: (res) => res.data?.products,
      enabled: relatedProductIds.length > 0,
    },
  );

  const pieData = useMemo(() => {
    return [
      {
        name: "Sales New to Brand",
        value: salesNtb,
      },
      {
        name: "Sales Repeat",
        value: salesRepeat,
      },
    ];
  }, [salesNtb, salesRepeat]);

  const columnSpecs = [
    {
      key: "halo_orders",
      options: {
        checked: true,
        isNegative: true,
        noColor: true,
        columnTitle: "Halo Orders",
        formatter: (v) => {
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(v)}
              <ProgressBar progress={(v / maxHaloOrders) * 100} width={65} />
            </span>
          );
        },
      },
    },
  ];

  const relatedProductsTable = useMemo(() => {
    if (relatedProductsData) {
      return relatedProductsData.map((p) => {
        const halo_orders = relatedProducts[p.id];
        return {
          ...p,
          halo_orders,
        };
      });
    }
    return [];
  }, [relatedProductsData, relatedProducts]);

  const maxHaloOrders = useMemo(() => {
    return Object.values(relatedProducts)?.sort((a, b) => b - a)?.[0] ?? 1;
  }, [relatedProducts]);

  return (
    <>
      <Col xs={4}>
        <h5 className="text-center">New To Brand vs. Repeat Sales</h5>
        <PieChart
          formatter={(v) => formatCurrency(v, marketPlace.marketPlace)}
          data={pieData}
          nameAccessor={(d) => d.name}
          accessor={(d) => d.value}
          colors={[metrics[0].color, metrics[1].color]}
        />
      </Col>
      <Col xs={8}>
        <h5 className="text-center">Halo Orders</h5>
        <BulkActionsTable
          defaultPageSize={5}
          defaultSorted={[{ id: "orders", desc: true }]}
          tableEntityType={"product"}
          tableId={"halo-products"}
          tableRef={tableRef}
          titleCol={{
            id: "products",
            accessor: "product_title",
            className: "text-start",
            width: 500,
            Header: "Products",
            isStatic: true,
            Cell: ({ value, row }) => {
              return (
                <ProductCell
                  value={value}
                  row={row}
                  original={{
                    ...row._original,
                    id: row._original?.product_id,
                  }}
                  showAsin={true}
                  showInventoryBadge={false}
                  showPricingBadge={true}
                  showBuyBoxBadge={true}
                />
              );
            },
            filterMethod: () => {
              return;
            },
            Filter: () => null,
          }}
          columnSpecs={columnSpecs}
          data={relatedProductsTable}
          isExpandable={false}
          isSelectable={false}
          hideFilterToggleIcon={true}
          hideResetFiltersIcon={true}
          getSearchCriteria={(row) => {
            const { product_title, sku, asin, id, tags } = row;
            return `${product_title} ${sku} ${asin} ${id} ${tags?.join(" ")}`;
          }}
          tableRowDescription={"Products"}
        />
      </Col>
    </>
  );
};

const ProductShopperInsights = ({ productId, product, marketPlace, showTabs = false }) => {
  const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB);
  const [selectedAudienceFormAsin, setSelectedAudienceFormAsin] = useState(false);

  const { group, start, end } = useDates();
  const channel = useAppSelector((state) => state.channel);

  const defaultFormData =
    selectedAudienceFormAsin ?
      {
        asins: [selectedAudienceFormAsin],
        lookalike_audience_expected_reach: "SIMILAR",
      }
    : {};

  const { data: categoriesData } = useCategoriesQuery(marketPlace.marketPlace, channel.currentChannel);

  const [filters, dimensions] = useMemo(() => {
    const filters = formatDatamartFilters({
      marketplace: marketPlace.marketPlace,
      product_id: productId,
      date__gte: start.format("YYYY-MM-DD"),
      date__lte: end.format("YYYY-MM-DD"),
    });
    return [filters, group];
  }, [productId, start, end, group, marketPlace]);

  const { data: productChartData } = useFetch(
    ["shopperingsights", "product", marketPlace.marketPlace, productId, dimensions, filters],
    "/api/data_report_v2/shopperinsights/stats/",
    {
      dimensions,
      filters,
    },
    {
      select: (res) => {
        return res.data.map((d) => ({ ...d, start_date: d.date })).sort((a, b) => moment(a.date) - moment(b.date));
      },
      default: [],
    },
  );

  const { data: productTotals } = useFetch(
    ["shopperingsights", "product", marketPlace.marketPlace, productId, "product_id", filters],
    "/api/data_report_v2/shopperinsights/stats/",
    {
      dimensions: "product_id",
      filters,
    },
    {
      select: (res) => {
        return res.data.map((d) => ({ ...d, start_date: d.date })).sort((a, b) => moment(a.date) - moment(b.date));
      },
      default: [],
    },
  );

  const metrics = useMemo(() => {
    return [
      buildMetric(marketPlace, productChartData, "sales_ntb"),
      buildMetric(marketPlace, productChartData, "sales_repeat"),
      buildMetric(marketPlace, productChartData, "orders_ntb"),
      buildMetric(marketPlace, productChartData, "orders_repeat"),
    ];
  }, [marketPlace, productChartData]);

  const hasAMC = useModuleEnabled("AMC");

  return (
    <Row>
      {showTabs ?
        <>
          {hasAMC && (
            <Row>
              <Col xs={12} className="d-flex justify-content-end" style={{ marginBottom: "-36px" }}>
                <div style={{ marginRight: "4px" }} className="mb-5 d-flex gap-5">
                  <CenteredModal
                    show={selectedAudienceFormAsin}
                    closeModal={() => setSelectedAudienceFormAsin(null)}
                    dialogClassName="modal-60w"
                    content={
                      <AudienceForm
                        setShowAudienceForm={setSelectedAudienceFormAsin}
                        defaultFormData={defaultFormData}
                      />
                    }
                  />
                  <AdPlanModal
                    initialAdPlan={{
                      product: [product.id],
                      categoryId: product.product_category,
                      preselectedProducts: true,
                    }}
                    isShortcut={true}
                    categories={categoriesData}
                    altModalButton={<button className={buttonRecipe()}>Create a New Media Plan</button>}
                  />
                  <Button onPress={() => setSelectedAudienceFormAsin(product.asin)}>Create Halo Audience</Button>
                </div>
              </Col>
            </Row>
          )}
          <Tabs selectedTab={selectedTab} tabs={TABS} onTabChange={setSelectedTab}>
            {selectedTab === "details" && (
              <Row>
                <Details
                  marketPlace={marketPlace}
                  productChartData={productChartData}
                  productTotals={productTotals}
                  metrics={metrics}
                  group={group}
                  productId={productId}
                  metricsPosition="bottom"
                />
              </Row>
            )}
            {selectedTab === "insights" && (
              <Row>
                <Insights
                  marketPlace={marketPlace}
                  productTotals={productTotals}
                  metrics={metrics}
                  productId={productId}
                />
              </Row>
            )}
            {selectedTab === "advertising" && (
              <Row>
                <AdvertisingSection productId={productId} marketPlace={marketPlace} />
              </Row>
            )}
          </Tabs>
        </>
      : <>
          <Details
            marketPlace={marketPlace}
            productChartData={productChartData}
            productTotals={productTotals}
            metrics={metrics}
            group={group}
            productId={productId}
          />
          <Insights marketPlace={marketPlace} productTotals={productTotals} metrics={metrics} productId={productId} />
        </>
      }
    </Row>
  );
};

export default ProductShopperInsights;
